import { type ReactNode } from "react";
import { Badge, Button, IconButton, styled } from "~/common/mui";
import classNames from "classnames";
import { TooltipWithArrow } from "~/common/components/TooltipWithArrow";
import { C_SECONDARY_HOVER } from "~/common/components/icon/Colors";

interface PersonalNavItemProps {
  name: string;
  icon?: ReactNode;
  isActive?: boolean;
  isNotification?: boolean;
  badgeContent?: string | number;
  badgeVariant?: "default" | "new";
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  "data-test-id"?: string;
  label?: string | null;
  "data-ta-spec"?: string;
  "data-ta-total"?: number | null;
  onMouseEnter?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

export const PersonalNavItem = (props: PersonalNavItemProps) => (
  <PersonalNavItemRoot
    onClick={props.onClick}
    data-test-id={props["data-test-id"]}
    className={classNames(
      "scha__personal-nav-item",
      !props.isNotification && {
        "scha__personal-nav-item--active": props.isActive,
      },
    )}
  >
    <PersonalNavItemDetails
      className={"scha__personal-nav-item__details" + (props.isNotification ? "__notification" : "")}
    >
      <TooltipWithArrow placement="top" title={props.name ? props.name : ""}>
        <Badge badgeContent={props.badgeContent ? props.badgeContent : null}>
          {props.label && (
            <Button
              variant="text"
              color="textSecondary"
              startIcon={props.icon}
              data-ta-spec={props["data-ta-spec"]}
              data-ta-total={props["data-ta-total"]}
            >
              {props.label}
            </Button>
          )}
          {props.icon && !props.label && (
            <IconButton
              sx={{ color: "text.secondary" }}
              data-ta-spec={props["data-ta-spec"]}
              data-ta-total={props["data-ta-total"]}
              onMouseEnter={props.onMouseEnter}
              onMouseLeave={props.onMouseLeave}
            >
              {props.icon}
            </IconButton>
          )}
        </Badge>
      </TooltipWithArrow>
    </PersonalNavItemDetails>
  </PersonalNavItemRoot>
);

const PersonalNavItemRoot = styled("div")(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
  color: theme.palette.text.secondary,
  cursor: "pointer",
  position: "relative",
  [theme.breakpoints.between("tablet", "desktop-s")]: {
    flexDirection: "row",
  },
  ".MuiBadge-badge": {
    top: 6,
    right: theme.spacing(1.5),
    backgroundColor: theme.palette.primary.main,
    color: `${theme.palette.primary.contrastText} !important`,
  },
  "&:hover": {
    ".scha__personal-nav-item__details__label": {
      color: `${C_SECONDARY_HOVER} !important`,
    },
    ".scha__personal-nav-item__name, .scha__personal-nav-item__details__quantity": {
      color: theme.palette.primary.main,
    },
  },
  "&.scha__personal-nav-item--active::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 3,
    backgroundColor: theme.palette.primary.main,
  },
  "& .scha__personal-nav-item__name": {
    marginLeft: theme.spacing(0.625),
    color: theme.palette.text.primary,
    ...theme.typography.bodyMedium,
    [theme.breakpoints.up("desktop-s")]: {
      marginLeft: 0,
      marginTop: theme.spacing(0.625),
    },
  },
}));

const PersonalNavItemDetails = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  position: "relative",
  i: {
    color: theme.palette.primary.main,
  },
  "&.scha__personal-nav-item__details__notification": {
    color: theme.palette.primary.main,
    ".MuiBadge-badge": {
      backgroundColor: theme.palette.salesCampaign.main,
    },
  },
  "& .scha__personal-nav-item__details__label": {
    ...theme.typography.titleSmall,
    marginLeft: theme.spacing(1),
    color: theme.palette.text.primary,
  },
}));
